import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cancelBooking } from "../../api/bookings";
import type { Booking } from "../../api/bookings";
import type { PendingSyncRetry } from "../../store/bookingsSlice";
import { format } from "date-fns";

interface CancelBookingDialogProps {
  booking: Booking | null;
  onClose: () => void;
  onSyncFailed?: (payload: PendingSyncRetry) => void;
}

export const CancelBookingDialog: React.FC<CancelBookingDialogProps> = ({
  booking,
  onClose,
  onSyncFailed,
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (bookingId: string) => cancelBooking(bookingId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      if (!result.calendar_synced && onSyncFailed && booking) {
        onSyncFailed({ bookingId: booking.booking_id, action: "cancel" });
      }
      onClose();
    },
  });

  const handleConfirm = () => {
    if (booking) mutation.mutate(booking.booking_id);
  };

  if (!booking) return null;

  return (
    <Dialog open={Boolean(booking)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Booking</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel the{" "}
          <strong>{booking.service_name}</strong> booking for{" "}
          <strong>{booking.customer_name}</strong> on{" "}
          {format(new Date(booking.preferred_date_time), "dd MMM yyyy 'at' HH:mm")}
          ?
        </DialogContentText>
        {mutation.isError && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            Failed to cancel booking. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Keep
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={mutation.isPending}
          startIcon={
            mutation.isPending ? <CircularProgress size={16} /> : undefined
          }
        >
          Confirm Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
